/**
 * ControlMachines — live control tab via WebSocket + REST commands.
 *
 * - Connects to machine_mqtt_data WebSocket for live output readings.
 * - Shows ONLY digital_outputs (toggle) and analog_outputs (setpoint input).
 * - Control commands sent via POST /api/machine_control/ (REST, not WebSocket).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import client from '../API/API';
import { Icon, ICONS } from '../components/ui/Icon';

function wsBase() {
  return window.location.origin.replace(/^https/, 'wss').replace(/^http/, 'ws');
}

// ── Status indicator ──────────────────────────────────────────────────────────
function StatusDot({ connected }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-sans)', fontSize: 12, color: connected ? 'var(--online)' : 'var(--ink-400)' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: connected ? 'var(--online)' : 'var(--border-strong)', boxShadow: connected ? '0 0 0 3px var(--online-bg)' : 'none' }} />
      {connected ? 'Live' : 'Connecting…'}
    </span>
  );
}

// ── Digital output card — toggle switch ───────────────────────────────────────
function DigitalCard({ channel, machineid }) {
  const isOn = channel.value === 1 || channel.value === true || channel.value === '1' || channel.value === 'ON';
  const [sending, setSending] = useState(false);
  const [flash,   setFlash]   = useState(null); // 'ok' | 'err'

  const toggle = async () => {
    if (sending) return;
    setSending(true);
    setFlash(null);
    try {
      await client.post('/api/machine_control/', {
        machine_id: machineid,
        channel:    channel.name,
        value:      isOn ? 0 : 1,
      });
      setFlash('ok');
    } catch {
      setFlash('err');
    } finally {
      setSending(false);
      setTimeout(() => setFlash(null), 2000);
    }
  };

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: `1px solid ${flash === 'err' ? 'var(--offline-border)' : 'var(--border-hair)'}`,
      borderRadius: 'var(--r-lg)', padding: '14px 16px', boxShadow: 'var(--sh-sm)',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      {/* Label */}
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--ink-400)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {channel.name}
      </div>

      {/* Live reading */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: isOn ? 'var(--online)' : 'var(--border-strong)', flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: isOn ? 'var(--online)' : 'var(--ink-500)' }}>
          {isOn ? 'ON' : 'OFF'}
        </span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--ink-300)', marginLeft: 4 }}>current</span>
      </div>

      {/* Command row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: flash === 'ok' ? 'var(--online)' : flash === 'err' ? 'var(--offline)' : 'var(--ink-400)' }}>
          {flash === 'ok' ? '✓ Sent' : flash === 'err' ? '✗ Failed' : 'Command'}
        </span>
        {/* Toggle switch */}
        <button
          onClick={toggle}
          disabled={sending}
          title={`Turn ${isOn ? 'OFF' : 'ON'}`}
          style={{
            width: 44, height: 24, borderRadius: 12, border: 'none',
            cursor: sending ? 'wait' : 'pointer',
            background: isOn ? 'var(--online)' : 'var(--border-strong)',
            position: 'relative', transition: 'background 0.2s',
            flexShrink: 0, opacity: sending ? 0.6 : 1,
          }}
        >
          <span style={{
            position: 'absolute', top: 3,
            left: isOn ? 23 : 3,
            width: 18, height: 18, borderRadius: '50%', background: '#fff',
            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </button>
      </div>
    </div>
  );
}

// ── Analog output card — setpoint input ──────────────────────────────────────
function AnalogCard({ channel, machineid }) {
  const [input,   setInput]   = useState('');
  const [sending, setSending] = useState(false);
  const [flash,   setFlash]   = useState(null);

  // Pre-fill with live value on first arrival
  useEffect(() => {
    if (channel.value != null && input === '') {
      setInput(String(channel.value));
    }
  }, [channel.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (sending || input === '') return;
    setSending(true);
    setFlash(null);
    try {
      await client.post('/api/machine_control/', {
        machine_id: machineid,
        channel:    channel.name,
        value:      parseFloat(input),
      });
      setFlash('ok');
    } catch {
      setFlash('err');
    } finally {
      setSending(false);
      setTimeout(() => setFlash(null), 2000);
    }
  };

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: `1px solid ${flash === 'err' ? 'var(--offline-border)' : 'var(--border-hair)'}`,
      borderRadius: 'var(--r-lg)', padding: '14px 16px', boxShadow: 'var(--sh-sm)',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {/* Label */}
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--ink-400)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {channel.name}
      </div>

      {/* Live value */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600, color: 'var(--ink-900)' }}>
          {channel.value ?? '—'}
        </span>
        {channel.unit && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-400)' }}>{channel.unit}</span>}
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--ink-300)', marginLeft: 4 }}>live</span>
      </div>

      {/* Setpoint row */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          type="number"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Set value…"
          style={{
            flex: 1, minWidth: 0,
            fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-900)',
            background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--r-md)', padding: '6px 10px', outline: 'none',
          }}
        />
        <button
          onClick={send}
          disabled={sending || input === ''}
          style={{
            padding: '6px 14px', borderRadius: 'var(--r-md)', border: 'none',
            background: flash === 'ok' ? 'var(--online)' : flash === 'err' ? 'var(--offline)' : 'var(--accent)',
            color: '#fff', fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 600,
            cursor: (sending || input === '') ? 'not-allowed' : 'pointer',
            opacity: (sending || input === '') ? 0.5 : 1,
            transition: 'background 0.2s', flexShrink: 0, minWidth: 48,
          }}
        >
          {sending ? '…' : flash === 'ok' ? '✓' : flash === 'err' ? '✗' : 'Set'}
        </button>
      </div>
    </div>
  );
}

// ── Section grid ──────────────────────────────────────────────────────────────
function SectionGrid({ title, items, type, machineid }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--ink-400)', marginBottom: 12 }}>
        {title}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
        {items.map((ch, i) =>
          type === 'digital'
            ? <DigitalCard key={i} channel={ch} machineid={machineid} />
            : <AnalogCard  key={i} channel={ch} machineid={machineid} />
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const ControlMachines = ({ machineid, nodeid }) => {
  const [connected,   setConnected]   = useState(false);
  const [digitalOuts, setDigitalOuts] = useState([]);
  const [analogOuts,  setAnalogOuts]  = useState([]);
  const [timestamp,   setTimestamp]   = useState(null);
  const [hasData,     setHasData]     = useState(false);
  const socketRef = useRef(null);

  const handleMessage = useCallback((event) => {
    try {
      const msg     = JSON.parse(event.data);
      const payload = msg.payload;
      if (!payload) return;

      const dOuts = payload.digital_outputs || [];
      const aOuts = payload.analog_outputs  || [];

      setDigitalOuts(dOuts);
      setAnalogOuts(aOuts);
      setTimestamp(payload.Timestamp || payload.timestamp || null);
      setHasData(dOuts.length + aOuts.length > 0);
    } catch { /* ignore parse errors */ }
  }, []);

  useEffect(() => {
    if (!machineid) return;
    const uid = localStorage.getItem('userid') || '';
    const url = `${wsBase()}/machine_mqtt_data/?machine_id=${machineid}&user_id=${uid}`;
    const ws  = new WebSocket(url);
    socketRef.current = ws;

    ws.onopen    = () => setConnected(true);
    ws.onclose   = () => setConnected(false);
    ws.onerror   = () => setConnected(false);
    ws.onmessage = handleMessage;

    return () => ws.close();
  }, [machineid, handleMessage]);

  if (!hasData) return (
    <div style={{ padding: '60px 0', textAlign: 'center' }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--bg-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: 'var(--ink-300)' }}>
        <Icon d={ICONS.wifi} size={22} />
      </div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 500, color: 'var(--ink-500)', marginBottom: 6 }}>No control data</div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-300)', marginBottom: 16 }}>Waiting for live data from the machine…</div>
      <StatusDot connected={connected} />
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <StatusDot connected={connected} />
        {timestamp && (
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-400)' }}>
            Updated: {new Date(timestamp).toLocaleTimeString()}
          </div>
        )}
      </div>

      <SectionGrid title="Digital Outputs" items={digitalOuts} type="digital" machineid={machineid} />
      <SectionGrid title="Analog Outputs"  items={analogOuts}  type="analog"  machineid={machineid} />
    </div>
  );
};

export default ControlMachines;
